import { useNavigate, useSearchParams } from 'react-router-dom';
import { FlowTitle } from '@/components/ui/text';
import { useState, useRef } from 'react';
import { useAuthUser } from '@/hooks/useAuthUser';
import { LoadingSpinner } from '@/common/LoadingSpinner';
import { takeReturnPath } from '@/modules/auth/returnAfterLogin';
import { currentUserId } from '@/infrastructure/clients/BackendClient';
import { leaveAuthNotice } from '@/modules/auth/authNotice';
import LureoButton from '@/common/LureoButton';
import { useEnvironmentPreferences } from '@/hooks/useEnvironmentPreferences';
import { Checkbox } from '@/components/cms/ui/checkbox';
import { Choice } from '@/components/ui/choice';
import { CheckIcon } from '@phosphor-icons/react';
import { useServices } from '@/infrastructure/providers/ServiceProvider';
import { getSignupAttributionProperties } from '@/modules/attribution/attribution';
import { AuthShell, AuthField, AuthCrossLink, SignedInRedirect, TERMS_OF_SERVICE_URL, PRIVACY_POLICY_URL } from './AuthShell';
import { useAuthStatus } from './useAuthStatus';
import { SignupDesktopHandoff } from '@/components/handoff/SignupDesktopHandoff';
import { isMobileDevice } from '@/utils/deviceDetection';
import { TextLink } from '@/components/ui/text-button';
import { lureoButtonClass } from '@/common/lureoButtonClass';
import { cn } from '@/utils/utils';

const TERMS_REQUIRED = 'Please agree to the Terms of Service and Privacy Policy to sign up';
const WAITLIST_URL = 'https://4xveku8op19.typeform.com/to/puz59jjt';

const PRIVATE_HOST = /^(localhost|127\.0\.0\.1|10\.\d+\.\d+\.\d+|192\.168\.\d+\.\d+|172\.(1[6-9]|2\d|3[01])\.\d+\.\d+)$/;

function isSignupPreviewAllowed(searchParams: URLSearchParams): boolean {
  if (searchParams.get('preview') !== '1') return false;
  const host = window.location.hostname;
  return PRIVATE_HOST.test(host) || host.includes('dev') || host.includes('staging');
}

export const Signup = () => {
  const { status, show: showStatus, clearError } = useAuthStatus();
  const [password, setPassword] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [signupSuccessNeedsConfirmation, setSignupSuccessNeedsConfirmation] = useState(false);
  const [signupSuccessOnMobile, setSignupSuccessOnMobile] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  // Set once this page starts a sign-up: the session it sets is not a visitor who was signed in already
  const [signingUpHere, setSigningUpHere] = useState(false);
  const submitting = useRef(false);
  const navigate = useNavigate();
  const signupFormRef = useRef<HTMLFormElement>(null);
  const { environmentPreferences, loading: prefsLoading, failed: prefsFailed, retry: retryPrefs } = useEnvironmentPreferences();
  const { user, loading: authLoading } = useAuthUser();
  const services = useServices();
  const [searchParams] = useSearchParams();
  const preview = isSignupPreviewAllowed(searchParams);
  const signUpEnabled = Boolean(environmentPreferences?.sign_up_on) || preview;
  // Unknown is not closed: the waitlist shows only once a read says sign-up is off
  const prefsUnknown = !preview && !environmentPreferences && (prefsLoading || prefsFailed);

  const edit = (set: (value: string) => void) => (value: string) => {
    clearError();
    set(value);
  };

  const handleSignupKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onSignupSubmit(event as any);
    }
  };

  async function onSignupSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      showStatus('Please enter a valid email address');
      return;
    }

    if (!password) {
      showStatus('Password cannot be empty');
      return;
    }

    if (password.length < 6) {
      showStatus('Password must be at least 6 characters long');
      return;
    }

    if (!firstName.trim() || firstName.trim().length < 1) {
      showStatus('First name must be at least 1 character');
      return;
    }

    if (!lastName.trim() || lastName.trim().length < 1) {
      showStatus('Last name must be at least 1 character');
      return;
    }

    if (!agreedToTerms) {
      showStatus(TERMS_REQUIRED);
      return;
    }

    if (submitting.current) return;
    submitting.current = true;
    setSigningUpHere(true);
    setIsSigningUp(true);

    try {
      const signupResponse = await services.auth.signUp({
        email,
        password,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        marketingConsent,
      }, {
        onSlow: () => showStatus('This is taking longer than usual. Please keep this page open.', 'info'),
      });

      if (signupResponse.userUniqueId) {
        services.analytics.identify(signupResponse.userUniqueId);
        const attribution = getSignupAttributionProperties();
        services.analytics.setPersonProperties(attribution);
        services.analytics.trackLureoAppSignUp({ signup_method: 'email', ...attribution });
      }

      if (signupResponse.accessToken && signupResponse.refreshToken) {
        try {
          await services.auth.setSupabaseSession(
            signupResponse.accessToken,
            signupResponse.refreshToken
          );

          if (isMobileDevice()) {
            setSignupSuccessOnMobile(true);
          } else {
            navigate(takeReturnPath(currentUserId()) ?? '/dashboard', { replace: true });
          }
        } catch {
          // The account exists; only signing in to it here failed
          leaveAuthNotice({ tone: 'info', message: 'Your account is ready. Please sign in to continue.' });
          navigate('/login', { replace: true });
        }
      } else {
        setSignupSuccessNeedsConfirmation(true);
      }
    } catch (error) {
      submitting.current = false;
      setSigningUpHere(false);
      if (error instanceof Error) {
        showStatus(error.message);
      } else {
        showStatus('An error occurred during signup');
      }
    } finally {
      setIsSigningUp(false);
    }
  }

  const termsAgreedForOAuth = () => {
    if (agreedToTerms) return true;
    showStatus(TERMS_REQUIRED);
    return false;
  };

  if (signupSuccessOnMobile) {
    return <SignupDesktopHandoff onContinue={() => navigate(takeReturnPath(currentUserId()) ?? '/dashboard', { replace: true })} />;
  }

  if (!authLoading && user && !signingUpHere) return <SignedInRedirect />;

  if (signupSuccessNeedsConfirmation) {
    return (
      <AuthShell
        showOAuth={false}
        onError={showStatus}
        footer={<AuthCrossLink prompt="Confirmed already?" to="/login" action="Sign in" />}>
        <div className="flex flex-col items-center gap-4 pt-2 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-lureo-mint/20">
            <CheckIcon className="h-7 w-7 text-lureo-success-ink" />
          </div>
          <FlowTitle as="h1">Check your inbox</FlowTitle>
          <p className="font-lureo-font-prose text-lureo-14 font-light leading-relaxed text-lureo-ink-subtle">
            We've sent a confirmation link to
          </p>
          <p className="break-all font-lureo-font-ui text-lureo-14 font-semibold text-lureo-ink leading-5">{email}</p>
          <p className="font-lureo-font-prose text-lureo-14 font-light leading-relaxed text-lureo-ink-subtle">
            Open it to confirm your account, then sign in to start using Lureo.
          </p>
          <p className="font-lureo-font-prose text-lureo-12 font-light text-lureo-ink-subtlest leading-4">
            Nothing after a few minutes? Check spam, or contact support.
          </p>
        </div>
      </AuthShell>
    );
  }

  if (prefsUnknown) {
    return (
      <AuthShell
        title="Sign up"
        subtitle={prefsFailed ? "We couldn't check whether sign-up is open." : 'Create your Lureo account.'}
        showOAuth={false}
        status={status}
        onError={showStatus}
        footer={<AuthCrossLink prompt="Already have an account?" to="/login" action="Sign in" />}>
        {prefsFailed ? (
          <LureoButton variant="secondary" className="h-10 w-full rounded-lureo-radius-button-pill" onClick={retryPrefs}>
            Try again
          </LureoButton>
        ) : (
          <div role="status" aria-busy="true" className="flex justify-center py-6">
            <LoadingSpinner tone="brand" />
          </div>
        )}
      </AuthShell>
    );
  }

  if (!signUpEnabled) {
    return (
      <AuthShell
        title="Sign up"
        subtitle="We're not open to new accounts here yet."
        showOAuth={false}
        status={status}
        onError={showStatus}
        footer={<AuthCrossLink prompt="Already have an account?" to="/login" action="Sign in" />}>
        <p className="text-center font-lureo-font-prose text-lureo-15 font-light leading-relaxed text-lureo-ink-subtle">
          We're still accepting sign ups to our waitlist. Join and we'll be in touch when your account is ready.
        </p>
        <a href={WAITLIST_URL} target="_blank" rel="noopener noreferrer" className={PRIMARY_LINK}>
          Join the waitlist
        </a>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Sign up"
      subtitle="Create your Lureo account."
      status={status}
      marketingConsent={marketingConsent}
      canStartOAuth={termsAgreedForOAuth}
      onError={showStatus}
      footer={<AuthCrossLink prompt="Already have an account?" to="/login" action="Sign in" />}>
      <form ref={signupFormRef} onSubmit={onSignupSubmit} className="flex flex-col gap-3.5">
        <div className="grid grid-cols-2 gap-2.5">
          <AuthField
            id="signup-first-name"
            label="First name"
            type="text"
            value={firstName}
            onChange={edit(setFirstName)}
            autoComplete="given-name"
            disabled={isSigningUp}
          />
          <AuthField
            id="signup-last-name"
            label="Last name"
            type="text"
            value={lastName}
            onChange={edit(setLastName)}
            autoComplete="family-name"
            disabled={isSigningUp}
          />
        </div>
        <AuthField
          id="signup-email"
          label="Email"
          type="email"
          value={email}
          onChange={edit(setEmail)}
          autoComplete="email"
          disabled={isSigningUp}
        />
        <AuthField
          id="signup-password"
          label="Password"
          type="password"
          value={password}
          onChange={edit(setPassword)}
          onKeyDown={handleSignupKeyDown}
          autoComplete="new-password"
          placeholder="At least 6 characters"
          disabled={isSigningUp}
        />
        <div className="flex flex-col gap-2.5 pt-1">
          <Choice
            className="items-start text-left"
            control={
              <Checkbox
                checked={agreedToTerms}
                onCheckedChange={(checked) => {
                  clearError();
                  setAgreedToTerms(checked === true);
                }}
                disabled={isSigningUp}
              />
            }
            label={
              <>
                I agree to the{' '}
                <TextLink
                  href={TERMS_OF_SERVICE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}>
                  Terms of Service
                </TextLink>
                {' and '}
                <TextLink
                  href={PRIVACY_POLICY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}>
                  Privacy Policy
                </TextLink>
              </>
            }
          />
          <Choice
            className="items-start text-left"
            control={
              <Checkbox
                checked={marketingConsent}
                onCheckedChange={(checked) => setMarketingConsent(checked === true)}
                disabled={isSigningUp}
              />
            }
            label="I'd like to receive product updates and marketing emails from Lureo."
          />
        </div>
      </form>
      <LureoButton
        variant="secondary"
        className="h-10 w-full rounded-lureo-radius-button-pill"
        disabled={isSigningUp}
        onClick={() => signupFormRef.current?.requestSubmit()}>
        {isSigningUp ? 'Signing up...' : 'Sign up'}
      </LureoButton>
    </AuthShell>
  );
};

// LureoButton's secondary (primary action) look on a link that must stay a link
const PRIMARY_LINK = cn(lureoButtonClass('secondary'), 'h-10 w-full justify-center rounded-lureo-radius-button-pill');
